import { v4 as uuidv4 } from 'uuid';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IProductAssignment, IProductGamePlanDetails, IProductPlanRequest, ProductPlanCropType, SeedListBrandApplication } from '../../Models/Responses/ProductGamePlan';
import { getFieldlessSeedAllocations, getProductGamePlan } from '../Plans/ProductGamePlanActions';
import { calculatorFromStore } from '../../Utility/Pricing/useProductRollup';
import { ProductRollupCalc } from '../../Utility/Pricing/ProductRollupCalc';
import { cropBrandAssociations, ICropBrandAssociation, isCorn, isEnogen, isSoy } from '../../../pages/FieldActivities/CropBrandAssociation';
import { cornId, CropConfig, soyId } from '../Seeds/CropsSlice';
import { getSeedingRateForSeriesAndRates, ISeriesYields } from '../Seeds/PlantingRateSlice';
import { RoundDownToTenths, RoundTargetYield } from '../../Utility/CalculationUtilities';
import { autoSelectTreatmentWithHybrid, BrandLookup, HybridLookup, MaterialLookup, SeriesLookup, treatmentKey, TreatmentLookup } from '../Seeds/SeedLookups';
import { actionWithRootState } from '../../Utility/actionWithRootState';
import { isEmpty } from 'lodash';
import { IRecommendationOptions } from './IRecommendationOptions';
import { LocalPosition } from '../../Models/Responses/SeedResponseV7';

interface IProductGamePlanStateV7 {
	selectedPlanId: string | undefined;

	/** Key is planId */
	plans: Record<string, {
		plan: ReturnType<typeof getProductGamePlan.fulfilled>['payload'] | undefined;
		edits: IPlanEdits | undefined;
		isNewPlan: boolean;
		planId: string;
	}>;

	/** Redux persist does not easily allow nested configs so there's only going to be one loading flag and one error message for all plan loading */
	isLoadingPlan: boolean;
	loadingPlanErrorMessage?: string;

	// Recommendation loading flags and error
	isLoadingRecommendation: boolean;
	loadingRecommendationErrorMessage?: string;
}

/** Everything the UI needs to manage the edits to a plan. */
interface IPlanEdits 
{
	/** Key is a generated UUID. */
	products: Record<string, IPlanProductLine>;

	/** 
	 * Key is literally: `${cropId}${brandApplication}` 
	 * @see recommendationOptionsKey() 
	 */
	recommendationOptions : {
		[cropIdAndBrandApplication: string]: IRecommendationOptions & {
			cropBrand: ICropBrandAssociation;
		}
	},
}

const initialState: IProductGamePlanStateV7 = {
	selectedPlanId: undefined,
	plans: {},
	isLoadingPlan: false,
	loadingPlanErrorMessage: undefined,
	isLoadingRecommendation: false,
	loadingRecommendationErrorMessage: undefined,
};

export interface IPlanProductLine extends IProductPlanLineEditables
{
	/** 
	 * Unique key for the line, not necessarily a unique product because users are allowed
	 * to add the same product multiple times.
	 */
	key: string;
	
	/** Enough info to uniquely identify a product within the seeds list. */
	brandId: string;
	cropId: string;
	seriesId: string;
	hybridId: string;
	/** Id provided by API. Only unique within a hybrid. */
	treatmentId: string;
}

/** All editable information in a plan line. */
interface IProductPlanLineEditables
{
	localPositioning: string | undefined;
	pricePerUnit: number | undefined;
	priceUnit: 'Acre' | 'Bag' | undefined;
	totalPrice: number | undefined;
	
	/** Acres. */
	appliedAcres: number | undefined;
	/** # of bags */
	quantity: number | undefined;
	/** Seeds per acre. */
	seedingRate: number | undefined;
	/** bu per acre */
	targetYield: number | undefined;
}

export const productGamePlanSliceV7 = createSlice({
	name: 'productGamePlanV7',
	initialState,
	reducers: {

		clearProductGamePlanV7State: (state) => 
		{
			state.selectedPlanId = undefined;
			state.plans = {};
			state.isLoadingPlan = false;
			state.loadingPlanErrorMessage = undefined;
		},

		clearLoadingPlanErrorMessage: (state) => 
		{
			state.loadingPlanErrorMessage = undefined;
		},

		clearLoadingRecommendationErrorMessage: (state) =>
		{
			state.loadingRecommendationErrorMessage = undefined;
		},

		setSelectedPlanId: (state, { payload }: PayloadAction<{ planId: string | undefined }>) => 
		{
			state.selectedPlanId = payload.planId;
		},

		/**
		 * Create a new plan locally. 
		 */
		createNewPlan: (state, { payload }: PayloadAction<{ 
			planId: string,
			growerZip: string,
		}>) => 
		{
			const { planId, growerZip } = payload;
			state.plans[planId] = {
				planId,
				edits: {
					products: {},
					recommendationOptions: recommendationOptionsDefaults(growerZip),
				},
				plan: undefined,
				isNewPlan: true,
			};
		},
		/** 
		 * Convert existing data on a plan to a format the UI can edit.
		 * Assumes the getProductGamePlan has run.
		 * 
		 * Do not use directly see wrapping thunk.
		 * 
		 * This could not be done in the extraReducer for fetching a plan because we need
		 * access to seed data from the seed slice.
		 */
		loadPlanForEditing:(state, { payload }: PayloadAction<{ 
			planId: string;

			// From thunk.
			calculator: ProductRollupCalc;
			hybrids: HybridLookup;
			series: SeriesLookup;
			treatments: TreatmentLookup;
			materials: MaterialLookup;
			brands: BrandLookup;
			localPositioning: { [key: string]: LocalPosition[] };
		}>) => 
		{
			const plan = state.plans[payload.planId];
			if (!plan)
				throw new Error('Attempted to load a plan that has not been downloaded yet.');

			plan.edits = loadEdits(payload.hybrids, payload.treatments, payload.materials, payload.brands, plan.plan, payload.localPositioning);
			
			// Recalc pricing.
			for (const product of Object.values(plan.edits.products))
			{
				applyProductLineUpdates(
					payload.calculator,
					payload.series,
					product,
					{},
					undefined,
				);
			}
		},

		/** 
		 * Add a new series to the indicated plan. 
		 * Do not use directly see wrapping thunk.
		 * 
		 * Used in + button
		 * Used in recommendation generation
		 */
		addSeries: (state, { payload }: PayloadAction<{
			planId: string;
			seriesId: string;
			hybridId?: string;
			treatmentId?: string;
			productName?: string;
			line?: Partial<IProductPlanLineEditables>;
			plantingRates?: ISeriesYields;

			calculator: ProductRollupCalc;
			series: SeriesLookup;
			hybrids: HybridLookup;
			treatments: TreatmentLookup;
			materials: MaterialLookup;
			brands: BrandLookup;
			localPositioning: { [key: string]: LocalPosition[]};
		}>) => 
		{
			const plan = state.plans[payload.planId];
			
			// Find a suitable product if one wasnt selected.
			// TODO: When the new endpoint for default treatment is finished, pass that in to autoselect so it can trump all other selection criteria.
			const autoSelectedTreatment = autoSelectTreatmentWithHybrid(payload.hybrids, payload.treatments, payload.materials, payload.brands, payload.seriesId, payload.hybridId);

			const selectedTreatment = payload.treatmentId && payload.hybridId 
				? payload.treatments[treatmentKey({ TreatmentId: payload.treatmentId, HybridId: payload.hybridId })] 
				: undefined;

			const treatment = selectedTreatment ?? autoSelectedTreatment;

			const serverLocalPositioning = payload.localPositioning[treatment.SeriesId] ? payload.localPositioning[treatment.SeriesId].flatMap(lp => lp.Position).join('\n') : '';

			const product: IPlanProductLine = {
				key: uuidv4(),
				brandId: treatment.BrandId,
				cropId: treatment.CropId,
				seriesId: treatment.SeriesId,
				hybridId: treatment.HybridId,
				treatmentId: treatment.TreatmentId,
				pricePerUnit: treatment.PricePerUnit,
				priceUnit: treatment.PricingUnit,
				localPositioning: serverLocalPositioning,
				
				appliedAcres: undefined,
				targetYield: undefined,
				seedingRate: undefined,
				quantity: undefined,
				totalPrice: undefined,
			};

			// If line information was provided, make sure it's correct by using the calculator.
			if (payload.line && (!!payload.line.targetYield || !!payload.line.appliedAcres))
			{
				applyProductLineUpdates(
					payload.calculator,
					payload.series,
					product,
					payload.line,
					payload.plantingRates,
				);
			}

			plan.edits.products[product.key] = product;
		},

		/** Remove all products in a series from a plan. */
		removeSeries: (state, { payload }: PayloadAction<{ planId: string; seriesId: string; }>) =>
		{
			const edits = state.plans[payload.planId].edits;
			for (const [key, product] of Object.entries(edits.products))
				if (product.seriesId === payload.seriesId)
					delete edits.products[key];
		},

		/**
		 * Hybrids are series + trait so this may be called Add Trait in the UI.
		 * Do not use directly see wrapping thunk.
		 */
		addProductLine: (state, { payload }: PayloadAction<{ 
			planId: string; 
			hybridId: string; 
			treatmentId?: string;

			// From thunk.
			hybrids: HybridLookup;
			treatments: TreatmentLookup;
			materials: MaterialLookup;
			brands: BrandLookup;
			localPositioning: { [key: string]: LocalPosition[] };
		}>) =>
		{
			const hybrid = payload.hybrids[payload.hybridId];
			
			// Make sure a treatment gets selected.
			const treatment = payload.treatmentId 
				? payload.treatments[treatmentKey(payload.treatmentId, hybrid.HybridId)] 
				: autoSelectTreatmentWithHybrid(payload.hybrids, payload.treatments, payload.materials, payload.brands, hybrid.SeriesId, hybrid.HybridId);
				
			const plan = state.plans[payload.planId];

			const serverLocalPositioning = payload.localPositioning[hybrid.SeriesId] ? payload.localPositioning[hybrid.SeriesId].flatMap(lp => lp.Position).join('\n') : '';

			const key = uuidv4();
			plan.edits.products[key] = {
				key,
				brandId: treatment.BrandId,
				cropId: treatment.CropId,
				seriesId: treatment.SeriesId,
				hybridId: treatment.HybridId,
				treatmentId: treatment.TreatmentId,
				pricePerUnit: treatment.PricePerUnit,
				priceUnit: treatment.PricingUnit,
				localPositioning: serverLocalPositioning,
				
				appliedAcres: 0,
				targetYield: 0,
				seedingRate: 0,
				quantity: 0,
				totalPrice: 0,
			};
		},

		/** 
		 * Update an existing product line's treatment.
		 * Do not use directly see wrapping thunk.
		 */
		updateTreatment:(state, { payload }: PayloadAction<{ 
			planId: string; 
			productKey: string; 
			treatmentKey: string;

			// From thunk
			calculator: ProductRollupCalc;
			series: SeriesLookup;
			treatments: TreatmentLookup;
		}>) =>
		{
			const plan = state.plans[payload.planId];

			const product = plan.edits.products[payload.productKey];
			const treatment = payload.treatments[payload.treatmentKey];
			product.treatmentId = treatment.TreatmentId;
			product.pricePerUnit= treatment.PricePerUnit;
			product.priceUnit= treatment.PricingUnit;
			
			// Recalc pricing
			applyProductLineUpdates(
				payload.calculator,
				payload.series,
				product,
				{},
				undefined,
			);
		},

		/** 
		 * Update an existing series local positioning (each product gets updated)
		 */
		updateLocalPositioning:(state, { payload }: PayloadAction<{ 
			planId: string | undefined; 
			seriesId: string | undefined;
			localPositioningStatement: string;
		}>) =>
		{
			const plan = state.plans[payload.planId];
			const allPlanProducts = plan.edits.products;
			const filteredProducts = Object.values(allPlanProducts).filter(p => p.seriesId === payload.seriesId);

			filteredProducts.forEach(product =>
			{
				product.localPositioning = payload.localPositioningStatement;
			});
		},

		/** 
		 * Update an existing product line. 
		 * Do not use directly see wrapping thunk.
		 */
		updateProductLine: (state, { payload }: PayloadAction<{ 
			planId: string; 
			productKey: string; 
			line: Partial<IProductPlanLineEditables>;
			plantingRates?: ISeriesYields;

			// From thunk
			calculator: ProductRollupCalc;
			series: SeriesLookup;
		}>) =>
		{
			const plan = state.plans[payload.planId];

			const product = plan.edits.products[payload.productKey];

			applyProductLineUpdates(
				payload.calculator,
				payload.series,
				product,
				payload.line,
				payload.plantingRates
			);
		},

		removeProductLine: (state, { payload }: PayloadAction<{ 
			planId: string; 
			key: string; 
		}>) =>
		{
			const plan = state.plans[payload.planId];
			delete plan.edits.products[payload.key];
		},

		/** Clear all added products. */
		clearProductLines: (state, { payload }: PayloadAction<{ 
			planId: string; 
		}>) =>
		{
			const plan = state.plans[payload.planId];
			for (const key of Object.keys(plan.edits.products))
				delete plan.edits.products[key];
		},

		updateRecommendationOptions: (state, { payload }: PayloadAction<{ 
			planId: string; 
			cropId: string; 
			brandApplication: SeedListBrandApplication; 
			recommendationOptions: Partial<IRecommendationOptions>;
		}>) =>
		{
			const plan = state.plans[payload.planId];
			const options = plan.edits.recommendationOptions[recommendationOptionsKey(payload.cropId, payload.brandApplication)];
		
			for (const [key, value] of Object.entries(payload.recommendationOptions))
			{
				options[key] = value;
			}
		},

		/** Reset recommendation options for a plan + crop brand to defaults. */
		resetRecommendationOptions: (state, { payload }: PayloadAction<{ 
			planId: string; 
			cropId: string; 
			brandApplication: SeedListBrandApplication; 
		}>) =>
		{
			const plan = state.plans[payload.planId];
			const key = recommendationOptionsKey(payload);
			const existing = plan.edits.recommendationOptions[key];
			const defaults = recommendationOptionsDefaults(existing.zip)[key];
			plan.edits.recommendationOptions[key] = defaults;
		},
	},

	extraReducers: (builder) => 
	{
		builder.addCase(getProductGamePlan.pending, (state, { meta, payload }) =>
		{
			const planId = meta.arg.planId;
			if (!state.plans[planId])
			{
				state.plans[planId] = {
					planId,
					edits: undefined,
					plan: undefined,
					isNewPlan: false,
				};
			}

			state.isLoadingPlan = true;
			state.loadingPlanErrorMessage = undefined;
			state.plans[planId].edits = undefined;

		});
		builder.addCase(getProductGamePlan.fulfilled, (state, { meta, payload }) =>
		{
			const planId = meta.arg.planId;
			
			state.loadingPlanErrorMessage = undefined;
			state.isLoadingPlan = false;
			state.plans[planId].plan = payload;

			// Edits will be loaded in a separate action that must be called from a UI component.

		});
		builder.addCase(getProductGamePlan.rejected, (state, { meta, payload }) =>
		{
			state.isLoadingPlan = false;
			state.loadingPlanErrorMessage = payload && (payload as string).indexOf('network') > -1 ? 
				'There was a problem contacting the server to get the Acre Proposal. Please refresh and try again.'
				: 'There was a problem getting the Acre Proposal. Please refresh and try again.';

		});

		// Recommendations
		builder.addCase(getFieldlessSeedAllocations.pending, (state, { meta, payload }) =>
		{
			state.isLoadingRecommendation = true;
		});
		builder.addCase(getFieldlessSeedAllocations.fulfilled, (state, { meta, payload }) =>
		{
			state.isLoadingRecommendation = false;
		});
		builder.addCase(getFieldlessSeedAllocations.rejected, (state, { meta, payload }) =>
		{
			state.isLoadingRecommendation = false;
			if (payload)
			{
				if ((payload as string).indexOf('network') > -1)
				{
					state.loadingRecommendationErrorMessage = 'There was a problem contacting the server to get the Recommendation. Please refresh and try again.';
				}
				else
				{
					state.loadingRecommendationErrorMessage = payload as string;
				}
			}
			else
			{
				state.loadingRecommendationErrorMessage = 'There was a problem downloading the Recommendation. Please refresh and try again.';
			}
		});
	},
});

/** Load any existing data from a plan into .edits */
function loadEdits(
	hybridLookup: HybridLookup,
	treatmentLookup: TreatmentLookup,
	materialLookup: MaterialLookup,
	brandLookup: BrandLookup,
	plan: IProductGamePlanDetails & {
		defaultTreatmentName: string;
		growerZip: string;
	},
	localPositioning: { [key: string]: LocalPosition[] }
): IPlanEdits
{
	if (!treatmentLookup || !hybridLookup)
		throw new Error('Tried to load plan data before seed data is present');

	const allTreatments = Object.values(treatmentLookup);
	// Convert existing recommendations into something editable.
	const existingProducts = plan.recommendations
		.flatMap(r => r.products)
		// Only load products that we have data for.
		// On productplans endpoint product_id is hybridId.
		.reduce((acc, p) => 
		{
			if (hybridLookup[p.product_id])
				acc.valid.push(p);
			else
				acc.invalid.push(p);

			return acc;
		}, { valid: [], invalid: [] } as { valid:IProductAssignment[], invalid: IProductAssignment[] });
		
	if (existingProducts.invalid.length > 0)
		console.log('Ignoring invalid products', existingProducts.invalid);
	
	const products: Record<string, IPlanProductLine> = existingProducts.valid.map(p => 
	{
		const hybrid = hybridLookup[p.product_id];
		// For now we're matching on treatmentName as a stopgap. Ideally we want to store treatmentId in the API and match on that.
		const planTreatment = allTreatments.find(t => t.HybridId === hybrid.HybridId && t.TreatmentId === p.treatmentId);
		if (!planTreatment)
		{
			console.warn(`Could not find a treatment that was on a plan. HybridId: ${p.product_id} TreatmentId: ${p.treatmentId}`);
		}

		// Auto select a treatment if we cant find the associated treatment.
		const treatment = planTreatment ?? autoSelectTreatmentWithHybrid(hybridLookup, treatmentLookup, materialLookup, brandLookup, hybrid.SeriesId, hybrid.HybridId);

		const serverLocalPositioning = localPositioning[hybrid.SeriesId] ? localPositioning[hybrid.SeriesId].flatMap(lp => lp.Position).join('\n') : '';

		const localPositioningText = p.statement ? p.statement : serverLocalPositioning ?? '';

		return {
			key: uuidv4(),
			brandId: hybrid.BrandId,
			cropId: hybrid.CropId,
			seriesId: hybrid.SeriesId,
			hybridId: hybrid.HybridId,
			treatmentId: treatment.TreatmentId,
			appliedAcres: p.applied_area,
			localPositioning: localPositioningText,
			quantity: p.quantity,
			quantityRequired: p.quantity,
			seedingRate: p.seed_rate,
			totalPrice: p.total_price,
			targetYield: p.yield_target,
			// Ignore whatever price is on the plan itself. Could be out of date.
			pricePerUnit: treatment.PricePerUnit,
			priceUnit: treatment.PricingUnit,
		};
	})
		.reduce((acc,cur) => 
		{
			acc[cur.key] = cur;
			return acc;
		}, {} as Record<string, IPlanProductLine>);
	
	// Extract recommendation options for all crop brands.
	const options = recommendationOptionsDefaults(plan.growerZip);
	// Override with data saved on the plan.
	for (const [key, option] of Object.entries(options))
	{
		const association = option.cropBrand;

		if (plan.RecommendationSettings && !isEmpty(plan.RecommendationSettings))
		{
			if (isEnogen(association))
			{
				options[key].acres = plan.RecommendationSettings.EnogenAcres;
				options[key].irrigated = plan.RecommendationSettings.EnogenIrrigated;
				options[key].seedsRequired = plan.RecommendationSettings.EnogenRequiredSeeds;
				options[key].targetYield = plan.RecommendationSettings.EnogenTargetYield;
				options[key].zip = plan.RecommendationSettings.EnogenZipCode;
			}
			else if (isCorn(association))
			{
				options[key].acres = plan.RecommendationSettings.CornAcres;
				options[key].irrigated = plan.RecommendationSettings.CornIrrigated;
				options[key].seedsRequired = plan.RecommendationSettings.CornRequiredSeeds;
				options[key].targetYield = plan.RecommendationSettings.CornTargetYield;
				options[key].zip = plan.RecommendationSettings.CornZipCode;
			}
			else if (isSoy(association))
			{
				options[key].acres = plan.RecommendationSettings.SoybeanAcres;
				options[key].irrigated = plan.RecommendationSettings.SoybeanIrrigated;
				options[key].seedsRequired = plan.RecommendationSettings.SoybeanRequiredSeeds;
				options[key].targetYield = plan.RecommendationSettings.SoybeanTargetYield;
				options[key].zip = plan.RecommendationSettings.SoybeanZipCode;
			}	
		}

		if (plan.AgronomicSettings && !isEmpty(plan.AgronomicSettings))
		{
			const setting = plan.AgronomicSettings
				.find(setting => setting.BrandApplication === association.brandApplication && setting.CropId === association.cropId);
			if (setting)
			{
				options[key].fungicide = setting.Fungicide;
				options[key].isFilterApplied = setting.IsFilterApplied;
				options[key].performance = setting.ConsistencyPerf;
				options[key].rmMax = setting.RmMax;
				options[key].rmMin = setting.RmMin;
				options[key].topEndYield = setting.TopEndYield;
				options[key].selectedTraits = setting.Traits.split(',').filter(Boolean);
			}
		}
	}
	
	return {
		products,
		recommendationOptions: options,
	};
}

/**
 * Convert the local edits to something the API can handle. Basically the reverse of @see loadEdits 
 */
export function planEditsToApi(
	brandLookup: BrandLookup, 
	plan: IProductGamePlanStateV7['plans'][any]
): IProductPlanRequest
{
	// Products
	const products: IProductPlanRequest['Products'] = Object.values(plan.edits.products)
		.map(p => ({
			AppliedArea: p.appliedAcres,
			// There are only two associations we care about: GH/NK and Enogen. 
			// If its not enogen then its gh/nk.
			BrandAssociation: 
				(brandLookup[p.brandId].Brand.toLowerCase().includes('enogen')
					? 'Enogen'
					: 'GH,NK'
				) as ProductPlanCropType,
			CropId: p.cropId,
			HybridId: p.hybridId,
			LocalPositioning: p.localPositioning,
			PriceRate: p.pricePerUnit,
			Quantity: p.quantity,
			// Quantity Required is unused so leaving it empty for now.
			QuantityRequired: 0, 
			SeedingRate: p.seedingRate,
			TotalPrice: p.totalPrice,
			YieldTarget: p.targetYield,
			TreatmentId: p.treatmentId,
		}));

	// Recommendation and agronomic settings.
	const agronomicsSettings: IProductPlanRequest['AgronomicSettings'] = [];
	const recommendationSettings:IProductPlanRequest['RecommendationSettings'] = {};
	for (const [key, options] of Object.entries(plan.edits.recommendationOptions))
	{
		const association = options.cropBrand;
		
		let brandApplication = 'Any';
		let cropId = cornId;
		if (isEnogen(association))
		{
			recommendationSettings.EnogenAcres = options.acres;
			recommendationSettings.EnogenIrrigated = options.irrigated;
			recommendationSettings.EnogenRequiredSeeds = options.seedsRequired;
			recommendationSettings.EnogenTargetYield = options.targetYield;
			recommendationSettings.EnogenZipCode = options.zip;

			brandApplication = 'Enogen';
			cropId = cornId;
		}
		else if (isCorn(association))
		{
			recommendationSettings.CornAcres = options.acres;
			recommendationSettings.CornIrrigated = options.irrigated;
			recommendationSettings.CornRequiredSeeds = options.seedsRequired;
			recommendationSettings.CornTargetYield = options.targetYield;
			recommendationSettings.CornZipCode = options.zip;

			brandApplication = 'Any';
			cropId = cornId;
		}
		else if (isSoy(association))
		{
			recommendationSettings.SoybeanAcres = options.acres;
			recommendationSettings.SoybeanIrrigated = options.irrigated;
			recommendationSettings.SoybeanRequiredSeeds = options.seedsRequired;
			recommendationSettings.SoybeanTargetYield = options.targetYield;
			recommendationSettings.SoybeanZipCode = options.zip;

			brandApplication = 'Any';
			cropId = soyId;
		}	

		// For each unique entry in the rec options table, generate agronomics settings.
		agronomicsSettings.push({
			CropId: cropId,
			BrandApplication: brandApplication,
			ProductPlanId: plan.planId,
			Fungicide: options.fungicide,
			IsFilterApplied: options.isFilterApplied,
			ConsistencyPerf: options.performance,
			RmMax: options.rmMax,
			RmMin: options.rmMin,
			TopEndYield: options.topEndYield,
			Traits: options.selectedTraits.join(),
		});
	}

	return {
		Products: products,
		RecommendationSettings: recommendationSettings,
		AgronomicSettings: agronomicsSettings,
	};
}

/** Generate the default set of recommendation options. */
const recommendationOptionsDefaults = (growerZip: string) => 
{
	const options: IPlanEdits['recommendationOptions'] = {};
	for (const cropBrand of cropBrandAssociations)
	{
		options[recommendationOptionsKey(cropBrand)] = {
			cropBrand: cropBrand,
			acres: CropConfig()[cropBrand.cropId].defaultFieldlessAcres,
			fungicide: false,
			irrigated: false,
			isFilterApplied: false,
			performance: (isEnogen(cropBrand) || isSoy(cropBrand)) ? 0 : 5,
			rmMax: undefined,
			rmMin: undefined,
			seedsRequired: CropConfig()[cropBrand.cropId].defaultFieldlessSeedsRequired,
			selectedTraits: [],
			targetYield: CropConfig()[cropBrand.cropId].defaultFieldlessYield,
			topEndYield: (isEnogen(cropBrand) || isSoy(cropBrand)) ? 0 : 5,
			zip: growerZip,
		};
	}

	return options;
};

/** 
 * Modifies the passed in product line with provided updates 
 * @param plantingRates Automatically set target yield if appropriate when provided.
 */
function applyProductLineUpdates(
	calculator: ProductRollupCalc, 
	seriesLookup: SeriesLookup,
	product: IPlanProductLine,
	updates: Partial<IProductPlanLineEditables>,
	plantingRates?: ISeriesYields,
)
{
	const { maxYield, minYield, hasPlantingRateCalculator, seedsPerBag, defaultSeedingRate } = CropConfig()[product.cropId];

	// Set min and max target yield if the value entered is too low or high.
	if (updates.targetYield !== undefined)
	{
		updates.targetYield = RoundTargetYield(updates.targetYield > maxYield
			? maxYield
			: updates.targetYield < minYield
				? minYield
				: updates.targetYield);
	}
	const targetYield =  updates.targetYield ?? product.targetYield ?? 0;

	// Automatically derive the seeding rate from the target yield if we can.
	// Also update the rate if the target yield is being updated.
	// If no plantingRates then we are probably just selecting a new treatment so do not update the seeding rate.
	if (plantingRates !== undefined && (!product.seedingRate || updates.targetYield !== undefined))
	{
		const fromPlantingRates = hasPlantingRateCalculator && plantingRates && Object.keys(plantingRates).length > 0 
			? getSeedingRateForSeriesAndRates(plantingRates, seriesLookup[product.seriesId].SeriesName, targetYield)
			: undefined;

		// TODO: Grab the grower default seeding rate and use it before using the default seeding rate.
		updates.seedingRate = fromPlantingRates ?? defaultSeedingRate;
	}

	const seedingRate = updates.seedingRate ?? product.seedingRate ?? 0;

	// Calculator only works on acres so if the user updates the bags, calc the equivalent acres.
	if (updates.quantity !== undefined)
	{
		// Acres rounds to the tenths. We round down in this special case only because we want to ensure that the bag # doesnt round up after going through the calc.
		updates.appliedAcres = RoundDownToTenths(updates.quantity * seedsPerBag / seedingRate);
	}

	const appliedAcres =  updates.appliedAcres ?? product.appliedAcres ?? 0;

	const results = calculator.calculateLine({
		cropId: product.cropId,
		hybridId: product.hybridId,
		treatmentId: product.treatmentId,
		targetYield,
		appliedAcres,
		seedingRate,
	});

	product.appliedAcres = results.appliedAcres;
	product.targetYield = results.targetYield;
	product.seedingRate = results.seedingRate;
	product.quantity = results.rounded.totalBags;
	product.totalPrice = results.rounded.discountedTotalCost;
}


/** Generate the key for recommendationOptions: ${cropId}${brandApplication}  */
export function recommendationOptionsKey(upper: { CropId: string; BrandApplication: SeedListBrandApplication }): string
export function recommendationOptionsKey(lower: { cropId: string; brandApplication: SeedListBrandApplication }): string
export function recommendationOptionsKey(cropId: string, brandApplication: SeedListBrandApplication) : string
export function recommendationOptionsKey(a: Object | string, b?: SeedListBrandApplication): string
{
	if (typeof a === 'object')
	{
		if (a.hasOwnProperty('CropId'))
		{
			const { CropId, BrandApplication } = a as unknown as any;
			return `${CropId}${BrandApplication}`;
		}
		else if (a.hasOwnProperty('cropId'))
		{
			const { cropId, brandApplication } = a as unknown as any;
			return `${cropId}${brandApplication}`;
		}
		else
		{
			throw new Error('Object does not fit any overload for this function');
		}
	}
	else 
	{
		const cropId = a;
		const brandApplication = b;
		return `${cropId}${brandApplication}`;
	}
}

export const loadPlanForEditing = actionWithRootState(
	productGamePlanSliceV7.actions.loadPlanForEditing, 
	state => ({ 
		calculator: calculatorFromStore(state).calculator,
		series: state.seeds.series,
		hybrids: state.seeds.hybrids,
		treatments: state.seeds.treatments,
		materials: state.seeds.materials,
		brands: state.seeds.brands,
		localPositioning: state.seeds.localPositioning,
	})
);

/** Wraps addSeries action to pull items from state. */
export const addSeries = actionWithRootState(
	productGamePlanSliceV7.actions.addSeries,
	state => 
	{
		const { calculator } = calculatorFromStore(state);

		return {
			calculator,
			series: state.seeds.series,
			treatments: state.seeds.treatments,
			hybrids: state.seeds.hybrids,
			materials: state.seeds.materials,
			brands: state.seeds.brands,
			localPositioning: state.seeds.localPositioning,
		};
	}
);

export const addProductLine = actionWithRootState(
	productGamePlanSliceV7.actions.addProductLine, 
	state => ({ 
		hybrids: state.seeds.hybrids,
		treatments: state.seeds.treatments,
		materials: state.seeds.materials,
		brands: state.seeds.brands,
		localPositioning: state.seeds.localPositioning,
	})
);


/** Wraps updateTreatment action to pull items from state. */
export const updateTreatment = actionWithRootState(
	productGamePlanSliceV7.actions.updateTreatment, 
	state => ({ 
		calculator:  calculatorFromStore(state).calculator,
		series: state.seeds.series,
		treatments: state.seeds.treatments,
	})
);

/** Wraps updateProductLine action to pull items from state. */
export const updateProductLine = actionWithRootState(
	productGamePlanSliceV7.actions.updateProductLine,
	state => ({
		calculator:calculatorFromStore(state).calculator,
		series: state.seeds.series,
	})
);

// Export the slice actions for easy access
export const { 
	clearProductGamePlanV7State,
	clearLoadingPlanErrorMessage,
	clearLoadingRecommendationErrorMessage,
	createNewPlan,
	updateLocalPositioning,
	removeProductLine,
	removeSeries,
	setSelectedPlanId,
	updateRecommendationOptions,
	resetRecommendationOptions,
	clearProductLines,
} = productGamePlanSliceV7.actions;